import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { NavLink, useLocation } from "react-router";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { ProcessNumber } from "@/services/backend/numbers/components/process-number";
import t from "@/lang/lang";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { Process } from "@/services/backend/processes/types/process";
import { validate as uuidValidate } from "uuid";

/**
 * BreadcrumbsToDocument is a component attempting to build breadcrumbs
 * for a process page leading to a document. The breadcrumbs are derived
 * from the current location, but not based on it.
 * BreadcrumbsToDocument will attempt to build a list of breadcrumbs
 * in this format: {customerName}-{processNumber}-{documentKind}-{documentNumber}.
 * BreadcrumbsToDocument will further rely on multiple conventions:
 * - document id route parameters are named e.g. "offerId" for an offer document
 * - a customer page exists at /customers/{customerId}
 * - process routes are designed as {prefix}/{processId}/{documentKind (plural)}/{documentId}
 * - the translation function can translate textual route segments
 * BreadcrumbsToDocument is a sensible default, but may be customized as needed.
 */
export function BreadcrumbsToDocument({ process }: { process: Process }) {
  const location = useLocation();

  // extract the path prefix for everything before the process id
  const idx = location.pathname.indexOf(process.id);
  const prefix = location.pathname.slice(0, idx);

  // extract the document kind path segment, if it exists
  const fullDocumentPath = location.pathname.slice(idx + process.id.length + 1);
  const documentKind = fullDocumentPath.split("/")[0];
  const documentId = fullDocumentPath.split("/")[1];
  const documentId2 = fullDocumentPath.split("/")[2];

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            {process.customerId !== null ? (
              <NavLink to={`/customers/${process.customerId}`}>
                <CustomerName customerId={process.customerId} />
              </NavLink>
            ) : (
              <CustomerName customerId={process.customerId} />
            )}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden md:block" />
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <NavLink to={`${prefix}${process.id}`}>
              <ProcessNumber processId={process.id} className="font-mono" />
            </NavLink>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {documentKind && !documentId && (
          <>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>{t(documentKind)}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
        {documentId && (
          <>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <NavLink to={`${prefix}${process.id}/${documentKind}`}>
                  {t(documentKind)}
                </NavLink>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {!workOrderSubRoutes.includes(documentId) && (
              <>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <DocumentNumberById documentId={documentId} />
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </>
        )}
        {documentId2 && uuidValidate(documentId2) && (
          <>
            {workOrderSubRoutes.includes(documentId) && (
              <BreadcrumbSeparator className="hidden md:block" />
            )}
            <BreadcrumbItem>
              <BreadcrumbPage>
                <DocumentNumberById documentId={documentId2} />
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

const workOrderSubRoutes = ["planning", "execution", "completion"];
